// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {createExpandableMediaQueryStylesheet} from '../core/Metrics';
import CoreStyles from '../core/Styles';
import {FixedSpace} from '../core/Spacing';

export class Grid extends React.Component {
    static Variant = {
        Centered: "centered", // Default
        Stretched: "stretched",
    };
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            gridSystem: {
                display: "flex",
                flexDirection: "column",
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: CoreMetrics.Grid.HorizontalMargin,
                paddingRight: CoreMetrics.Grid.HorizontalMargin,
            },
            centered: {
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: CoreMetrics.Grid.FixedWidth,
                width: "calc(100% - 2 * " + CoreMetrics.Grid.HorizontalMargin + "px)"
            },
            stretched: {
            },
            external: {
            },
            internal: {
                marginLeft: 0,
                marginRight: 0,
                paddingLeft: 0,
                paddingRight: 0,
                width: "100%",
            },
        });

        let {variant, isInternal} = this.props;
        let combinedStyles = [
            styles.gridSystem,
            styles[variant],
            (isInternal) ? styles.internal : styles.external,
            ...this.props.styles,
        ];

        return (
            <div className={css(combinedStyles)}>
                {this.props.children}
            </div>
        );
    }
}
Grid.defaultProps = {
    variant: Grid.Variant.Centered,
    isInternal: false,
    styles: [],
};

export class GridRow extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            row: {
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
            },
        });

        let combinedStyles = [styles.row, ...this.props.styles];

        return (
            <div className={css(combinedStyles)}>
                {this.props.children}
            </div>
        );
    }
}
GridRow.defaultProps = {
    styles: [],
};

export class GridColumn extends React.Component {
    static Variant = {
        Default: "default", // Default variant
        OneHalf: "oneHalf",
        OneThird: "oneThird",
        OneFourth: "oneFourth",
        OneSixth: "oneSixth",
        OneEighth: "oneEighth",
        OneNinth: "oneNinth",
        OneTwelfth: "oneTwelfth",
    };
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            column: {
                margin: 0,
                padding: 0,
                marginRight: CoreMetrics.Grid.HorizontalGutter,
                ":last-of-type": {
                    marginRight: 0,
                },
            },
        });

        let variantStyle = CoreStyles.gridWidthFull;
        let {variant} = this.props;
        if (variant === GridColumn.Variant.OneHalf) {
            variantStyle = CoreStyles.gridWidthOneHalf;
        } else if (variant === GridColumn.Variant.OneThird) {
            variantStyle = CoreStyles.gridWidthOneThird;
        } else if (variant === GridColumn.Variant.OneFourth) {
            variantStyle = CoreStyles.gridWidthOneFourth;
        } else if (variant === GridColumn.Variant.OneSixth) {
            variantStyle = CoreStyles.gridWidthOneSixth;
        } else if (variant === GridColumn.Variant.OneEighth) {
            variantStyle = CoreStyles.gridWidthOneEighth;
        } else if (variant === GridColumn.Variant.OneNinth) {
            variantStyle = CoreStyles.gridWidthOneNinth;
        } else if (variant === GridColumn.Variant.OneTwelfth) {
            variantStyle = CoreStyles.gridWidthOneTwelfth;
        }

        let combinedStyles = [styles.column, variantStyle, ...this.props.styles];

        return (
            <div className={css(combinedStyles)}>
                {this.props.children}
            </div>
        );
    }
}
GridColumn.defaultProps = {
    variant: GridColumn.Variant.Default,
    styles: [],
};

export class OneColumnLayout extends React.Component {
    render() {
        const {variant, column1} = this.props;
        return (
            <div>
                <Grid variant={variant}>
                    <GridRow>
                        <GridColumn>{column1}</GridColumn>
                    </GridRow>
                </Grid>
            </div>
        );
    }
}

export class OneContentColumnLayout extends React.Component {
    render() {
        const {variant, column1} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            phone: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            tablet: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            desktop: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const phoneLayout = (
            <Grid variant={variant} styles={[styles.phone]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                </GridRow>
            </Grid>
        );
        const tabletLayout = (
            <Grid variant={variant} styles={[styles.tablet]}>
                <GridRow>
                    <GridColumn variant={GridColumn.Variant.OneEighth} />
                    <GridColumn>{column1}</GridColumn>
                    <GridColumn variant={GridColumn.Variant.OneEighth} />
                </GridRow>
            </Grid>
        );
        const desktopLayout = (
            <Grid variant={variant} styles={[styles.desktop]}>
                <GridRow>
                    <GridColumn variant={GridColumn.Variant.OneFourth} />
                    <GridColumn>{column1}</GridColumn>
                    <GridColumn variant={GridColumn.Variant.OneFourth} />
                </GridRow>
            </Grid>
        );
        return (
            <div>
                {phoneLayout}
                {tabletLayout}
                {desktopLayout}
            </div>
        );
    }
}

export class TwoColumnLayout extends React.Component {
    render() {
        const {variant, column1, column2} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            small: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            large: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const smallLayout = (
            <Grid variant={variant} styles={[styles.small]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column2}</GridColumn>
                </GridRow>
            </Grid>
        );
        const largeLayout = (
            <Grid variant={variant} styles={[styles.large]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                    <GridColumn>{column2}</GridColumn>
                </GridRow>
            </Grid>
        );
        return (
            <div>
                {smallLayout}
                {largeLayout}
            </div>
        );
    }
}

export class ThreeColumnLayout extends React.Component {
    render() {
        const {variant, column1, column2, column3} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            small: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            large: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const smallLayout = (
            <Grid variant={variant} styles={[styles.small]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column2}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column3}</GridColumn>
                </GridRow>
            </Grid>
        );
        const largeLayout = (
            <Grid variant={variant} styles={[styles.large]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                    <GridColumn>{column2}</GridColumn>
                    <GridColumn>{column3}</GridColumn>
                </GridRow>
            </Grid>
        );
        return (
            <div>
                {smallLayout}
                {largeLayout}
            </div>
        );
    }
}

export class FourColumnLayout extends React.Component {
    render() {
        const {variant, column1, column2, column3, column4} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            small: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            large: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const smallLayout = (
            <Grid variant={variant} styles={[styles.small]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column2}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column3}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{column4}</GridColumn>
                </GridRow>
            </Grid>
        );
        const largeLayout = (
            <Grid variant={variant} styles={[styles.large]}>
                <GridRow>
                    <GridColumn>{column1}</GridColumn>
                    <GridColumn>{column2}</GridColumn>
                    <GridColumn>{column3}</GridColumn>
                    <GridColumn>{column4}</GridColumn>
                </GridRow>
            </Grid>
        );
        return (
            <div>
                {smallLayout}
                {largeLayout}
            </div>
        );
    }
}

export class TOCandContentLayout extends React.Component {
    render() {
        const {variant, toc, content} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            small: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            large: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const smallLayout = (
            <Grid variant={variant} styles={[styles.small]}>
                <GridRow>
                    <GridColumn>{content}</GridColumn>
                </GridRow>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <GridRow>
                    <GridColumn>{toc}</GridColumn>
                </GridRow>
            </Grid>
        );
        const largeLayout = (
            <Grid variant={variant} styles={[styles.large]}>
                <GridRow>
                    <GridColumn variant={GridColumn.Variant.OneFourth}>{toc}</GridColumn>
                    <GridColumn>{content}</GridColumn>
                </GridRow>
            </Grid>
        );
        return (
            <div>
                {smallLayout}
                {largeLayout}
            </div>
        );
    }
}
TOCandContentLayout.defaultProps = {
    styles: [],
};


export class DockedTOCandContentLayout extends React.Component {
    render() {
        const {toc, content} = this.props;
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                display: "flex",
                flexDirection: "row-reverse",
                width: "100%",
                height: "100%",
            },
            smallSidebar: {
                display: "flex",
                flexDirection: "column",
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            largeSidebar: {
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                alignSelf: "stretch",
                width: CoreMetrics.Grid.SidebarWidth,
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
            content: {
                flexGrow: 1,
            },
        });

        let combinedStyles = [styles.container, ...this.props.styles];

        return (
            <div className={css(combinedStyles)}>
                <div className={css(styles.content)}>
                    {content}
                    <div className={css(styles.smallSidebar)}>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                        {toc}
                    </div>
                </div>
                <div className={css(styles.largeSidebar)}>
                    {toc}
                </div>
            </div>
        );
    }
}
DockedTOCandContentLayout.defaultProps = {
    styles: [],
};



// WEBPACK FOOTER //
// ./src/layouts/Grid.jsx